import React, { useEffect, useState, useCallback } from 'react';
import { Trash, Filter } from 'lucide-react';
import Input from 'components/ui/Input';
import Select from 'components/ui/Select';
import SelectInput from 'components/ui/SelectInput';
import DateTimePicker, {
  DateTimePickerValue,
} from 'components/ui/DateTimePicker';
import {
  SEGMENT_OPERATORS,
  SEGMENT_OPERATORS_GROUPS,
} from 'constants/segmentConstants';
import { Condition, ConditionValue } from 'types/segments/segments.types';
import {
  validateCondition,
  FieldValidationErrors,
  operatorRequiresValue,
} from 'utils/conditionValidation';
import {
  eventFieldFromAttribute,
  attributeFromEventField,
} from 'pages/CampaignBuilder/components/Trigger/eventConditionsHelpers';

export interface EventAttributeOption {
  value: string;
  label: string;
}

const OP = {
  HAS_PROPERTY: 'has_property',
  AT_LEAST_ONE: 'at_least_one',
  DEFAULT: 'default',
} as const;
const DEFAULTS = {
  [OP.HAS_PROPERTY]: { property: '', operator: 'equals', value: null },
  [OP.AT_LEAST_ONE]: { value: null, arrayType: '' },
  [OP.DEFAULT]: { value: null },
} as const;
const NO_VALUE_OPS = [
  'between',
  'is_timestamp',
  'at_least_one',
  'has_property',
  'is_not_timestamp',
  'is_before',
  'is_after',
  'is_between',
  'exists',
  'is_valid',
  'is_invalid',
  'not_exists',
  'is_empty',
  'is_not_empty',
];
const DATE_OPS_ONLY = [
  'is_timestamp',
  'is_not_timestamp',
  'exists',
  'not_exists',
  'is_empty',
  'is_not_empty',
  'is_valid',
  'is_invalid',
  'at_least_one',
  'has_property',
];

interface EventAttributesConditionProps {
  conditions: Record<string, Condition[]>;
  onConditionsChange: (conditions: Record<string, Condition[]>) => void;
  eventAttributeOptions: EventAttributeOption[];
  loading?: boolean;
}

const EventAttributesCondition = ({
  conditions,
  onConditionsChange,
  eventAttributeOptions,
  loading = false,
}: EventAttributesConditionProps) => {
  const [conditionType, setConditionType] = useState<'and' | 'or'>('and');
  const [fieldErrors, setFieldErrors] = useState<FieldValidationErrors>({});
  const [dateTimePickerOpen, setDateTimePickerOpen] = useState<{
    conditionIndex: number;
    parentPath: string;
    field?: 'start' | 'end';
  } | null>(null);

  useEffect(() => {
    setConditionType(
      (Object.keys(conditions || {})[0] as 'and' | 'or') || 'and'
    );
  }, [conditions]);

  const options = {
    'Event Attributes': eventAttributeOptions.map((opt) => ({
      label: opt.label,
      value: opt.value,
      tag: '',
    })),
  };

  const parseDateTimeFromValue = (value: ConditionValue): Date | null => {
    const datetimeValue = value?.datetime;
    if (datetimeValue instanceof Date) {
      return datetimeValue;
    } else if (typeof datetimeValue === 'string') {
      const dt = new Date(datetimeValue);
      if (!isNaN(dt.getTime())) {
        return dt;
      }
    }
    if (value?.date) {
      const dt = new Date(value.date);
      if (!isNaN(dt.getTime())) {
        return dt;
      }
    }
    return null;
  };

  const formatDateTimeLocal = (date: Date): { date: string; time: string } => {
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const formattedTime = date.toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
    return { date: formattedDate, time: formattedTime };
  };

  const formatDateTimeForDisplay = (value: ConditionValue): string => {
    const dt = parseDateTimeFromValue(value);
    if (!dt) {
      if (value?.date) {
        const dateOnly = new Date(value.date);
        if (!isNaN(dateOnly.getTime())) {
          return dateOnly.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          });
        }
      }
      return 'Select Date & Time';
    }

    if (value?.datetime) {
      const tz = value?.timezone || 'UTC';
      const formatter = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: tz,
        timeZoneName: 'short',
        hour12: true,
      });
      return formatter.format(dt);
    }

    return dt.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDateTimePickerValue = (
    value: ConditionValue
  ): DateTimePickerValue | null => {
    const dt = parseDateTimeFromValue(value);
    if (!dt) {
      return null;
    }

    return {
      datetime: dt,
      timestamp: value?.timestamp || Math.floor(dt.getTime() / 1000),
      timezone:
        value?.timezone ||
        Intl.DateTimeFormat().resolvedOptions().timeZone ||
        'UTC',
    };
  };

  const getFieldError = (
    conditionIndex: number,
    field: 'field' | 'operator' | 'value'
  ): string | undefined => {
    return fieldErrors[conditionType]?.[conditionIndex]?.[field];
  };

  const validateAndUpdateErrors = useCallback(() => {
    const newFieldErrors: FieldValidationErrors = {};
    const conditionArray = conditions[conditionType] || [];

    conditionArray.forEach((condition, index) => {
      const validation = validateCondition(condition);
      if (!validation.isValid) {
        newFieldErrors[conditionType] = {
          ...newFieldErrors[conditionType],
          [index]: validation.fieldErrors,
        };
      }
    });

    setFieldErrors(newFieldErrors);
  }, [conditions, conditionType]);

  useEffect(() => {
    validateAndUpdateErrors();
  }, [validateAndUpdateErrors, conditions]);

  const updateConditions = (
    updater: (conditions: Condition[]) => Condition[]
  ) => {
    const newConditions = {
      ...conditions,
      [conditionType]: updater(conditions[conditionType] || []),
    };
    onConditionsChange(newConditions);
  };

  const getOpType = (v: string): keyof typeof OP =>
    v === OP.HAS_PROPERTY
      ? 'HAS_PROPERTY'
      : v === OP.AT_LEAST_ONE
        ? 'AT_LEAST_ONE'
        : 'DEFAULT';

  const conditionUpdateForOp = (
    opVal: string,
    opType: keyof typeof OP
  ): Partial<Condition> => {
    const base = { operator: opVal, value: DEFAULTS[OP[opType]].value };
    if (opType === 'HAS_PROPERTY')
      return { ...base, value: DEFAULTS.has_property };
    if (opType === 'AT_LEAST_ONE')
      return {
        ...base,
        value: DEFAULTS.at_least_one.value,
        arrayType: DEFAULTS.at_least_one.arrayType,
      };
    return base;
  };

  const renderCondition = (
    condition: Condition,
    index: number,
    parentPath: string = '',
    updateParentCondition?: (
      updater: (condition: Condition) => Condition
    ) => void,
    arrayCondition?: string
  ) => {
    const { field, operator, value, arrayType, property } = condition;

    const attributeName = attributeFromEventField(field) ?? field ?? '';
    const timeFields = [
      'createdAt',
      'updatedAt',
      'time',
      'datetime',
      'timestamp',
    ];
    const disabledGroup =
      attributeName === 'email'
        ? SEGMENT_OPERATORS_GROUPS.UNIX_TIMESTAMP
        : timeFields.includes(attributeName) ||
            arrayCondition ||
            (field?.includes('attributes.') ?? false)
          ? SEGMENT_OPERATORS_GROUPS.EMAIL
          : '';

    const updateCondition = (updater: (condition: Condition) => Condition) => {
      if (updateParentCondition) {
        updateParentCondition(updater);
      } else {
        updateConditions((prevConditions) => {
          const updatedCondition = updater(prevConditions[index]);
          if (updatedCondition && (updatedCondition as any).shouldDelete) {
            return prevConditions.filter((_, i) => i !== index);
          }
          const newConditions = [...prevConditions];
          newConditions[index] = updatedCondition;
          return newConditions;
        });
      }
    };

    const onOpChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      const opVal = e.target.value;
      const opType = getOpType(opVal);
      const upd = conditionUpdateForOp(opVal, opType);
      updateCondition((prev: Condition) => {
        const next = { ...prev, ...upd };
        if (opType !== 'AT_LEAST_ONE' && 'arrayType' in next)
          delete next.arrayType;
        return next;
      });
    };

    const valueError = getFieldError(index, 'value');
    const isPropertyNameError =
      arrayCondition === 'propertyValue' &&
      (property === undefined ||
        property === null ||
        String(property).trim() === '');
    const isValueEmpty = value === null || value === undefined || value === '';
    const showValueErrorOnValueField =
      (arrayCondition && operatorRequiresValue(operator) && isValueEmpty) ||
      (valueError && valueError !== 'Property name is required');
    const valueErrorMessage =
      valueError && valueError !== 'Property name is required'
        ? valueError
        : 'Value is required for this operator';

    return (
      <span
        key={`${parentPath}-condition-${index}`}
        className={`w-full flex flex-col sm:flex-row sm:justify-between gap-2 text-black text-xs sm:text-sm rounded-lg duration-200
                ${arrayCondition ? 'ml-2 sm:ml-4' : 'bg-condition-background px-2 py-2 border border-gray-300 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all'}
            `}
      >
        <span className="flex flex-wrap gap-2 sm:gap-3 w-full min-w-0 items-start">
          {/* Event Attribute (replaces Field + no Data Source) */}
          {!arrayCondition && (
            <div className="w-full sm:w-auto sm:min-w-0 sm:max-w-[180px] flex-shrink-0 flex flex-col gap-0.5">
              <label className="text-muted-foreground font-medium text-xs mb-0.5">
                Event Attribute
              </label>
              <SelectInput
                options={options['Event Attributes']}
                placeholder={
                  loading
                    ? 'Loading attributes...'
                    : 'Select or type attribute name...'
                }
                value={attributeFromEventField(field) ?? field ?? ''}
                onChange={(value) => {
                  updateCondition((prev) => ({
                    ...prev,
                    field: eventFieldFromAttribute(value),
                  }));
                }}
                error={getFieldError(index, 'field') || undefined}
                disabled={loading}
              />
            </div>
          )}

          {arrayCondition && arrayCondition === 'propertyValue' && (
            <div className="w-full sm:w-auto sm:min-w-0 sm:max-w-[180px] flex-shrink-0 text-xs sm:text-sm flex flex-col gap-0.5">
              <label className="text-muted-foreground font-medium text-xs mb-0.5">
                Property Name
              </label>
              <Input
                placeholder="Property name"
                value={property || ''}
                onChange={(e) => {
                  updateCondition((prev) => ({
                    ...prev,
                    property: e.target.value,
                  }));
                }}
                className={`min-w-0 border rounded-lg px-2 py-1.5 sm:px-2.5 sm:py-2 text-xs sm:text-sm ${
                  isPropertyNameError
                    ? 'border-destructive focus:border-destructive focus:ring-destructive'
                    : 'border-gray-300'
                }`}
              />
              {isPropertyNameError && (
                <div className="text-xs text-destructive mt-0.5 flex items-center gap-1">
                  <span className="w-1 h-1 bg-destructive rounded-full shrink-0"></span>
                  Property name is required
                </div>
              )}
            </div>
          )}

          {/* Operator */}
          <div className="w-full sm:w-auto sm:min-w-0 sm:max-w-[180px] flex-shrink-0 text-xs sm:text-sm flex flex-col gap-0.5">
            <label className="text-muted-foreground font-medium text-xs mb-0.5">
              Operator
            </label>
            <Select
              options={SEGMENT_OPERATORS}
              placeholder="Operator"
              value={operator}
              disabledGroup={disabledGroup}
              onChange={onOpChange}
              className={
                getFieldError(index, 'operator')
                  ? 'border-destructive focus:border-destructive focus:ring-destructive'
                  : ''
              }
            />
            {getFieldError(index, 'operator') && (
              <div className="text-xs text-destructive mt-0.5 flex items-center gap-1">
                <span className="w-1 h-1 bg-destructive rounded-full shrink-0"></span>
                {getFieldError(index, 'operator')}
              </div>
            )}
          </div>

          {/* Array Type Selector */}
          {operator === 'at_least_one' && (
            <div className="w-full sm:w-auto sm:min-w-0 sm:max-w-[180px] flex-shrink-0 text-xs sm:text-sm flex flex-col gap-0.5">
              <label className="text-muted-foreground font-medium text-xs mb-0.5">
                Array Type
              </label>
              <Select
                options={[
                  { label: 'Array Value', value: 'arrayValue' },
                  { label: 'Property Value', value: 'propertyValue' },
                ]}
                placeholder="Select Field"
                value={arrayType}
                onChange={(e) => {
                  const propertyValue =
                    e.target.value === 'propertyValue'
                      ? { property: '', value: null }
                      : { value: null };
                  updateCondition((prev) => ({
                    ...prev,
                    arrayType: e.target.value,
                    value: {
                      operator: 'equals',
                      ...propertyValue,
                    },
                  }));
                }}
              />
            </div>
          )}

          {/* Array Value Condition */}
          {(arrayType || operator === 'has_property') && (
            <div className="w-full min-w-0 text-xs sm:text-sm">
              {renderCondition(
                value as Condition,
                index,
                parentPath,
                (updater) => {
                  updateCondition((prev) => ({
                    ...prev,
                    value: updater(prev.value as Condition),
                  }));
                },
                arrayType ?? 'propertyValue'
              )}
            </div>
          )}

          {!NO_VALUE_OPS.includes(operator) ? (
            <div className="w-full sm:w-auto sm:min-w-0 sm:max-w-[180px] flex-shrink-0 text-xs sm:text-sm flex flex-col gap-0.5">
              <label className="text-muted-foreground font-medium text-xs mb-0.5">
                Value
              </label>
              <Input
                placeholder="Value"
                value={(value as string) || ''}
                onChange={(e) => {
                  updateCondition((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }));
                }}
                className={`min-w-0 border rounded-lg px-2 py-1.5 sm:px-2.5 sm:py-2 text-xs sm:text-sm ${
                  showValueErrorOnValueField
                    ? 'border-destructive focus:border-destructive focus:ring-destructive'
                    : 'border-gray-300 focus:border-primary focus:ring-primary'
                }`}
              />
              {showValueErrorOnValueField && (
                <div className="text-xs text-destructive mt-0.5 flex items-center gap-1">
                  <span className="w-1 h-1 bg-destructive rounded-full shrink-0"></span>
                  {valueErrorMessage}
                </div>
              )}
            </div>
          ) : operator === 'is_between' ? (
            <div className="w-full min-w-0 flex-shrink-0 text-xs sm:text-sm flex flex-col gap-1">
              <label className="text-muted-foreground font-medium text-xs mb-0.5">
                Value
              </label>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-1.5 sm:gap-2">
                <button
                  type="button"
                  onClick={() => {
                    setDateTimePickerOpen({
                      conditionIndex: index,
                      parentPath: parentPath,
                      field: 'start',
                    });
                  }}
                  className="min-w-0 flex-1 sm:flex-none sm:min-w-[140px] border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg bg-white hover:bg-gray-50 text-left text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                >
                  {(value as ConditionValue)?.start
                    ? (() => {
                        const startValue = (value as ConditionValue).start!;
                        if (startValue.includes('T')) {
                          const dt = new Date(startValue);
                          return dt.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                        }
                        return new Date(startValue).toLocaleDateString(
                          'en-US',
                          {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                          }
                        );
                      })()
                    : 'Select Start Date'}
                </button>
                <span className="text-gray-500 text-xs sm:text-sm shrink-0">
                  to
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setDateTimePickerOpen({
                      conditionIndex: index,
                      parentPath: parentPath,
                      field: 'end',
                    });
                  }}
                  className="min-w-0 flex-1 sm:flex-none sm:min-w-[140px] border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg bg-white hover:bg-gray-50 text-left text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                >
                  {(value as ConditionValue)?.end
                    ? (() => {
                        const endValue = (value as ConditionValue).end!;
                        if (endValue.includes('T')) {
                          const dt = new Date(endValue);
                          return dt.toLocaleString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          });
                        }
                        return new Date(endValue).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        });
                      })()
                    : 'Select End Date'}
                </button>
                {dateTimePickerOpen?.conditionIndex === index &&
                  dateTimePickerOpen?.parentPath === parentPath &&
                  (dateTimePickerOpen?.field === 'start' ||
                    dateTimePickerOpen?.field === 'end') && (
                    <DateTimePicker
                      isOpen={true}
                      value={
                        dateTimePickerOpen.field === 'start' &&
                        (value as ConditionValue)?.start
                          ? (() => {
                              const startValue = (value as ConditionValue)
                                .start!;
                              const date = startValue.includes('T')
                                ? new Date(startValue)
                                : new Date(startValue);
                              return {
                                datetime: date,
                                timestamp:
                                  (value as ConditionValue).startTimestamp ||
                                  Math.floor(date.getTime() / 1000),
                                timezone:
                                  (value as ConditionValue).timezone || 'UTC',
                              };
                            })()
                          : dateTimePickerOpen.field === 'end' &&
                              (value as ConditionValue)?.end
                            ? (() => {
                                const endValue = (value as ConditionValue).end!;
                                const date = endValue.includes('T')
                                  ? new Date(endValue)
                                  : new Date(endValue);
                                return {
                                  datetime: date,
                                  timestamp:
                                    (value as ConditionValue).endTimestamp ||
                                    Math.floor(date.getTime() / 1000),
                                  timezone:
                                    (value as ConditionValue).timezone || 'UTC',
                                };
                              })()
                            : null
                      }
                      onChange={(dateTimeValue: DateTimePickerValue) => {
                        updateCondition((prev) => {
                          const currentValue = prev.value;
                          const field = dateTimePickerOpen.field!;
                          const timestampField =
                            field === 'start'
                              ? 'startTimestamp'
                              : 'endTimestamp';
                          const newValue =
                            typeof currentValue === 'object' &&
                            currentValue !== null
                              ? {
                                  ...currentValue,
                                  [field]: dateTimeValue.datetime.toISOString(),
                                  [timestampField]: dateTimeValue.timestamp,
                                  timezone: dateTimeValue.timezone,
                                }
                              : {
                                  [field]: dateTimeValue.datetime.toISOString(),
                                  [timestampField]: dateTimeValue.timestamp,
                                  timezone: dateTimeValue.timezone,
                                };
                          return { ...prev, value: newValue };
                        });
                      }}
                      onClose={() => setDateTimePickerOpen(null)}
                    />
                  )}
              </div>
            </div>
          ) : operator === 'between' ? (
            <div className="w-full min-w-0 flex-shrink-0 text-xs sm:text-sm flex flex-col gap-1">
              <label className="text-muted-foreground font-medium text-xs mb-0.5">
                Value
              </label>
              <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-1.5 sm:gap-2">
                <Input
                  placeholder="From"
                  value={(value as ConditionValue)?.start || ''}
                  onChange={(e) => {
                    updateCondition((prev) => {
                      const currentValue = prev.value;
                      const newValue =
                        typeof currentValue === 'object' &&
                        currentValue !== null
                          ? { ...currentValue, start: e.target.value }
                          : { start: e.target.value, end: '' };
                      return { ...prev, value: newValue };
                    });
                  }}
                  className="min-w-0 flex-1 border border-gray-300 px-2 py-1.5 sm:px-2.5 sm:py-2 text-xs sm:text-sm rounded-lg"
                />
                <span className="text-gray-500 text-xs sm:text-sm shrink-0">
                  to
                </span>
                <Input
                  placeholder="To"
                  value={(value as ConditionValue)?.end || ''}
                  onChange={(e) => {
                    updateCondition((prev) => {
                      const currentValue = prev.value;
                      const newValue =
                        typeof currentValue === 'object' &&
                        currentValue !== null
                          ? { ...currentValue, end: e.target.value }
                          : { start: '', end: e.target.value };
                      return { ...prev, value: newValue };
                    });
                  }}
                  className="min-w-0 flex-1 border border-gray-300 px-2 py-1.5 sm:px-2.5 sm:py-2 text-xs sm:text-sm rounded-lg"
                />
              </div>
            </div>
          ) : (
            !DATE_OPS_ONLY.includes(operator) && (
              <div className="flex flex-col gap-1.5 sm:gap-2 w-full min-w-0 text-xs sm:text-sm">
                <div className="flex flex-wrap gap-1.5 sm:gap-2 w-full">
                  <div className="w-full sm:w-auto sm:min-w-0 sm:max-w-[180px] flex-shrink-0 flex flex-col gap-0.5">
                    <label className="text-muted-foreground font-medium text-xs mb-0.5">
                      Value type
                    </label>
                    <Select
                      options={[
                        { label: 'Specific Value', value: 'specific_value' },
                        { label: 'Relative Date', value: 'relative_date' },
                      ]}
                      placeholder="Value type"
                      value={(value as ConditionValue)?.dateType || ''}
                      onChange={(e) => {
                        updateCondition((prev) => {
                          let newValue: any;
                          if (e.target.value === 'relative_date') {
                            newValue = {
                              dateType: 'relative_date',
                              days: '',
                              timeDirection: '',
                            };
                          } else if (e.target.value === 'specific_value') {
                            newValue = { dateType: 'specific_value', date: '' };
                          } else {
                            newValue = { dateType: '' };
                          }
                          return { ...prev, value: newValue };
                        });
                      }}
                    />
                  </div>
                  {(value as ConditionValue)?.dateType === 'relative_date' && (
                    <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-xs sm:text-sm items-end">
                      <span className="text-gray-500 text-xs sm:text-sm">
                        of
                      </span>
                      <div className="flex flex-col gap-0.5">
                        <label className="text-muted-foreground font-medium text-xs mb-0.5">
                          Days
                        </label>
                        <Input
                          type="number"
                          className="min-w-0 w-20 sm:w-24 border border-gray-300 px-2 py-1.5 sm:px-2.5 sm:py-2 text-xs sm:text-sm rounded-lg"
                          placeholder="Number"
                          value={(value as ConditionValue)?.days || ''}
                          onChange={(e) => {
                            updateCondition((prev) => {
                              const currentValue = prev.value;
                              const newValue =
                                typeof currentValue === 'object' &&
                                currentValue !== null
                                  ? { ...currentValue, days: e.target.value }
                                  : { days: e.target.value, timeDirection: '' };
                              return { ...prev, value: newValue };
                            });
                          }}
                        />
                      </div>
                      <span className="text-gray-500 px-0.5 text-xs sm:text-sm">
                        days
                      </span>
                      <div className="min-w-0 w-24 sm:w-28 flex flex-col gap-0.5">
                        <label className="text-muted-foreground font-medium text-xs mb-0.5">
                          When
                        </label>
                        <Select
                          options={[
                            { label: 'ago', value: 'ago' },
                            { label: 'from now', value: 'from_now' },
                          ]}
                          placeholder="When"
                          value={(value as ConditionValue)?.timeDirection || ''}
                          onChange={(e) => {
                            updateCondition((prev) => {
                              const currentValue = prev.value;
                              const newValue =
                                typeof currentValue === 'object' &&
                                currentValue !== null
                                  ? {
                                      ...currentValue,
                                      timeDirection: e.target.value,
                                    }
                                  : { days: '', timeDirection: e.target.value };
                              return { ...prev, value: newValue };
                            });
                          }}
                        />
                      </div>
                      {(() => {
                        const days = (value as ConditionValue)?.days;
                        const timeDirection = (value as ConditionValue)
                          ?.timeDirection;

                        if (days && timeDirection) {
                          const daysNum = parseInt(days, 10);
                          if (!isNaN(daysNum)) {
                            const currentDate = new Date();
                            const resultDate = new Date(currentDate);

                            if (timeDirection === 'from_now') {
                              resultDate.setDate(
                                currentDate.getDate() + daysNum
                              );
                            } else if (timeDirection === 'ago') {
                              resultDate.setDate(
                                currentDate.getDate() - daysNum
                              );
                            }

                            const formattedDate = resultDate.toLocaleDateString(
                              'en-GB',
                              {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              }
                            );

                            const formattedTime = resultDate.toLocaleTimeString(
                              'en-GB',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              }
                            );

                            return (
                              <div className="relative inline-block group">
                                <span className="text-xs sm:text-sm text-gray-600 ml-1 sm:ml-2 cursor-help underline decoration-dotted decoration-gray-400 hover:decoration-gray-600">
                                  Expected Output
                                </span>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1.5 sm:mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none whitespace-nowrap">
                                  <div className="w-auto min-w-[100px] p-2 sm:p-2.5 bg-foreground text-primary-foreground text-xs sm:text-sm rounded-lg shadow-xl">
                                    <div className="text-center">
                                      <div className="font-semibold mb-1 text-white">
                                        Date & Time
                                      </div>
                                      <div className="text-gray-200">
                                        {formattedDate} {formattedTime}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-0.5 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                                </div>
                              </div>
                            );
                          }
                        }
                        return null;
                      })()}
                    </div>
                  )}
                  {(value as ConditionValue)?.dateType === 'specific_value' && (
                    <div className="flex flex-col gap-0.5 sm:gap-1">
                      <label className="text-muted-foreground font-medium text-xs ml-0 sm:ml-4">
                        Date & time
                      </label>
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap text-xs sm:text-sm">
                        <span className="text-gray-500">of</span>
                        <button
                          type="button"
                          aria-label="Select date and time"
                          onClick={() => {
                            setDateTimePickerOpen({
                              conditionIndex: index,
                              parentPath: parentPath,
                            });
                          }}
                          className="min-w-0 flex-1 sm:flex-none sm:min-w-[180px] border border-gray-300 px-2 py-1.5 sm:px-3 sm:py-2 rounded-lg bg-white hover:bg-gray-50 text-left text-xs sm:text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        >
                          {formatDateTimeForDisplay(value as ConditionValue)}
                        </button>
                        {(() => {
                          const dt = parseDateTimeFromValue(
                            value as ConditionValue
                          );
                          if (dt) {
                            const { date: formattedDate, time: formattedTime } =
                              formatDateTimeLocal(dt);
                            return (
                              <div className="relative inline-block group">
                                <span className="text-xs sm:text-sm text-gray-600 ml-1 sm:ml-2 cursor-help underline decoration-dotted decoration-gray-400 hover:decoration-gray-600">
                                  Local Timezone
                                </span>
                                <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1.5 sm:mt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 pointer-events-none whitespace-nowrap">
                                  <div className="w-auto min-w-[100px] p-2 sm:p-2.5 bg-foreground text-primary-foreground text-xs sm:text-sm rounded-lg shadow-xl">
                                    <div className="text-center">
                                      <div className="font-semibold mb-1 text-white">
                                        Date & Time
                                      </div>
                                      <div className="text-gray-200">
                                        {formattedDate} {formattedTime}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-0.5 w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                                </div>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {dateTimePickerOpen?.conditionIndex === index &&
                          dateTimePickerOpen?.parentPath === parentPath &&
                          !dateTimePickerOpen?.field && (
                            <DateTimePicker
                              isOpen={true}
                              value={getDateTimePickerValue(
                                value as ConditionValue
                              )}
                              onChange={(
                                dateTimeValue: DateTimePickerValue
                              ) => {
                                updateCondition((prev) => {
                                  const currentValue = prev.value;
                                  const datetimeString =
                                    dateTimeValue.datetime.toISOString();
                                  const newValue =
                                    typeof currentValue === 'object' &&
                                    currentValue !== null
                                      ? {
                                          ...currentValue,
                                          datetime: datetimeString,
                                          timestamp: dateTimeValue.timestamp,
                                          timezone: dateTimeValue.timezone,
                                          date: datetimeString.split('T')[0],
                                        }
                                      : {
                                          dateType: 'specific_value',
                                          datetime: datetimeString,
                                          timestamp: dateTimeValue.timestamp,
                                          timezone: dateTimeValue.timezone,
                                          date: datetimeString.split('T')[0],
                                        };
                                  return { ...prev, value: newValue };
                                });
                              }}
                              onClose={() => setDateTimePickerOpen(null)}
                            />
                          )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          )}
        </span>

        {!arrayCondition && (
          <div className="flex justify-end shrink-0 self-start sm:self-center">
            <button
              onClick={() => {
                if (updateParentCondition) {
                  updateParentCondition(() => ({ shouldDelete: true }) as any);
                } else {
                  updateConditions((prevConditions) => {
                    const newConditions = [...prevConditions];
                    newConditions.splice(index, 1);
                    return newConditions;
                  });
                }
              }}
              className="p-1.5 sm:p-2 text-destructive hover:text-destructive/80 hover:bg-destructive-background rounded-lg transition-colors touch-manipulation"
              title="Remove condition"
            >
              <Trash className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        )}
      </span>
    );
  };

  const conditionArray = conditions[conditionType] || [];

  return (
    <div
      className={`flex flex-col w-full min-w-0 ${conditionArray.length > 0 ? 'border-t border-gray-200 pt-4 gap-1.5 sm:gap-2' : ''}`}
    >
      {conditionArray.length > 0 && (
        <div className="flex flex-wrap items-center gap-1">
          <span className="text-xs sm:text-sm text-gray-700 font-medium">
            with
          </span>
          <Select
            options={[
              { value: 'and', label: 'all' },
              { value: 'or', label: 'any' },
            ]}
            value={conditionType}
            onChange={(e) => {
              const type = e.target.value as 'and' | 'or';
              setConditionType(type);
              onConditionsChange({
                [type]: conditionArray,
              });
            }}
            className="min-w-[3.5rem] text-xs text-gray-700 border-gray-300 px-2 py-1"
          />
          <span className="text-xs sm:text-sm text-gray-700 font-medium">
            of the following event attributes
          </span>
        </div>
      )}

      <div className="flex flex-col gap-3 sm:gap-4 w-full min-w-0">
        {conditionArray.map((condition, index) => {
          return (
            <React.Fragment key={`root-condition-${index}`}>
              {renderCondition(condition, index)}
              {index < conditionArray.length - 1 && (
                <div className="flex items-center w-full">
                  <div className="flex-1 min-w-0 h-px bg-gray-300" />
                  <div className="px-2 sm:px-4 py-1 sm:py-1.5 mx-2 sm:mx-3 bg-primary/10 border border-primary/20 text-primary text-[10px] sm:text-xs font-semibold uppercase tracking-wide rounded-full shadow-sm shrink-0">
                    {conditionType === 'and' ? 'And' : 'Or'}
                  </div>
                  <div className="flex-1 min-w-0 h-px bg-gray-300" />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="flex flex-row">
        <button
          type="button"
          onClick={() => {
            updateConditions((prevConditions) => [
              ...prevConditions,
              {
                condition: 'event',
                field: '',
                operator: '',
                value: null,
              },
            ]);
          }}
          className="mt-2 flex items-center justify-center sm:justify-start gap-1.5 sm:gap-2 px-2.5 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-primary border border-border rounded-lg bg-card hover:bg-muted/50 transition-colors touch-manipulation w-full sm:w-auto"
        >
          <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary shrink-0" />
          Add event data filter
        </button>
      </div>
    </div>
  );
};

export default EventAttributesCondition;
